import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  ClinicalDocument,
  ClinicalDocumentCreate,
  ClinicalDocumentLight
} from "../models/document";
import { map } from "rxjs/operators";
import { values } from "lodash-es";
import { tryParseJson } from "src/app/support/json";

@Injectable()
export class DocumentBackend {
  constructor(private http: HttpClient) {}

  load(id: string): Observable<ClinicalDocument> {
    return this.http
      .get<ClinicalDocument>(
        `${environment.snoots.api.healthcare}/documents/${id}`
      )
      .pipe(map(extend));
  }

  loadLight(id: string): Observable<ClinicalDocumentLight> {
    return this.http
      .get<ClinicalDocumentLight>(
        `${environment.snoots.api.healthcare}/documents/${id}`,
        {
          params: {
            light: true
          }
        }
      )
      .pipe(map(extendLight));
  }

  list(request: PageRequest): Observable<PaginatedResult<ClinicalDocument>> {
    return this.http
      .get<PaginatedResult<ClinicalDocument>>(
        `${environment.snoots.api.healthcare}/documents`,
        {
          params: {
            p: request.page,
            s: request.pageSize,
            ...request.filterValues
          }
        }
      )
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: response.docs.map(extend)
          };
        })
      );
  }

  listLight(
    request: PageRequest
  ): Observable<PaginatedResult<ClinicalDocument>> {
    return this.http.get<PaginatedResult<ClinicalDocument>>(
      `${environment.snoots.api.healthcare}/documents`,
      {
        params: {
          light: true,
          p: request.page,
          s: request.pageSize
        }
      }
    );
  }

  listAll(): Observable<ClinicalDocument[]> {
    return this.listLight({ page: 1, pageSize: 10000 }).pipe(
      map((x) => x.docs)
    );
  }

  listAllLight(): Observable<ClinicalDocument[]> {
    return this.list({ page: 1, pageSize: 10000 }).pipe(map((x) => x.docs));
  }

  listAllByTypes(types: string[]): Observable<ClinicalDocument[]> {
    return this.listAll().pipe(
      map((x) => {
        return x.filter((doc) => types.includes(doc.type));
      })
    );
  }

  listAllByTypesLight(types: string[]): Observable<ClinicalDocumentLight[]> {
    return this.listAll().pipe(
      map((x) => {
        return x.filter((doc) => types.includes(doc.type));
      })
    );
  }

  @UserNotify({
    successMessage: marker("Documento creato"),
    errorMessage: marker("Impossibile creare il documento"),
    progressIndeterminate: true,
    progressMessage: marker("Crezione documento")
  })
  create(payload: ClinicalDocumentCreate): Observable<ClinicalDocument> {
    return this.http.post<ClinicalDocument>(
      `${environment.snoots.api.healthcare}/documents`,
      payload
    );
  }

  update(id: string, request: Partial<ClinicalDocument>): Observable<void> {
    const payload: any = request;

    if (request.content) {
      payload.content = JSON.stringify(request.content);
    }

    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/documents/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Documento eliminato"),
    errorMessage: marker("Impossibile eliminare il documento")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/documents/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Documento inviato"),
    errorMessage: marker("Impossibile inviare il documento")
  })
  send(
    id: string,
    payload: {
      email: string;
      customerId: string;
      patientName?: string;
      attachments: Array<{
        contentType: string;
        storageId: string;
        fileName: string;
      }>;
    }
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/documents/${id}`,
      payload
    );
  }
}

function extend(document: ClinicalDocument): ClinicalDocument {
  const jsonParsed = tryParseJson<{
    html: string;
    ignoreLetterhead: boolean;
  }>(<any>document.content, {
    html: <any>document.content || "",
    ignoreLetterhead: false
  });

  return {
    ...document,
    modules: document.modules ? values(document.modules) : [],
    content: <any>jsonParsed
  };
}

function extendLight(document: ClinicalDocumentLight): ClinicalDocumentLight {
  const jsonParsed = tryParseJson<{
    html: string;
    ignoreLetterhead: boolean;
  }>(<any>document.content, {
    html: <any>document.content || "",
    ignoreLetterhead: false
  });

  return {
    ...document,
    modules: document.modules ? values(document.modules) : [],
    content: <any>jsonParsed
  };
}
